import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box, Container, Typography, Card, Stack, Button,
  TextField, InputAdornment, MenuItem, Select, Alert,
  CircularProgress, Fade, Paper, IconButton
} from '@mui/material';
import {
  PhoneAndroid, ArrowBack, Message, WhatsApp,
  CheckCircle, Shield, LockReset, Campaign
} from '@mui/icons-material';

import { useAuth } from '../contexts/AuthContext';

// --- Vibrant Brand Colors ---
const primaryGradient = 'linear-gradient(135deg, #FF6A00 0%, #EE0979 100%)';
const bgSoft = '#F4F7FB';
const textDark = '#1E293B';
const textMuted = '#64748B';

export default function Login() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectParam = searchParams.get('redirect');

  const { sendOTP, verifyOTP, resendOTP } = useAuth();

  // Local States
  const [step, setStep] = useState(1); // 1 = Mobile Input, 2 = OTP Input
  const [mobile, setMobile] = useState('');
  const [countryCode, setCountryCode] = useState('+91');
  const [otpMethod, setOtpMethod] = useState('sms');
  const [otp, setOtp] = useState('');

  const [localLoading, setLocalLoading] = useState(false);
  const [alertInfo, setAlertInfo] = useState({ show: false, message: '', type: 'info' });
  const [resendTimer, setResendTimer] = useState(0);

  // Handle Resend Timer countdown
  useEffect(() => {
    let interval;
    if (resendTimer > 0) {
      interval = setInterval(() => setResendTimer((prev) => prev - 1), 1000);
    }
    return () => clearInterval(interval);
  }, [resendTimer]);

  const showAlert = (message, type = 'info') => {
    setAlertInfo({ show: true, message, type });
    setTimeout(() => setAlertInfo({ show: false, message: '', type: 'info' }), 4000);
  };

  const handleMobileChange = (e) => {
    const value = e.target.value.replace(/[^0-9]/g, '');
    if (value.length <= 10) setMobile(value);
  };

  const handleOtpChange = (e) => {
    const value = e.target.value.replace(/[^0-9]/g, '');
    if (value.length <= 6) setOtp(value);
  };

  // 🌟 API Integration: Send OTP 🌟
  const handleSendOTP = async (e) => {
    e.preventDefault();
    if (!mobile || mobile.length < 10) {
      return showAlert('कृपया 10 अंकों का सही मोबाइल नंबर दर्ज करें।', 'error');
    }

    setLocalLoading(true);

    // API Call
    const response = await sendOTP({
      mobile: mobile,
      country_code: parseInt(countryCode.replace('+', '')),
      // अगर बैकएंड में otpMethod (sms/whatsapp) चाहिए तो यहाँ पास करें: method: otpMethod
    });

    console.log("Otp data response", response);

    setLocalLoading(false);

    if (response.success) {
      setStep(2);
      setResendTimer(60);
      showAlert(response.message || 'OTP सफलतापूर्वक भेज दिया गया है!', 'success');
    } else {
      showAlert(response.message || 'OTP भेजने में विफल। कृपया पुनः प्रयास करें।', 'error');
    }
  };

  // 🌟 API Integration: Verify OTP 🌟
  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    if (!otp || otp.length < 6) {
      return showAlert('कृपया 6 अंकों का OTP दर्ज करें।', 'error');
    }

    setLocalLoading(true);

    // API Call
    const response = await verifyOTP({
      mobile: mobile,
      otp_code: otp,
      country_code: parseInt(countryCode.replace('+', ''))
    });

    console.log("verify otp response", response);

    setLocalLoading(false);

    if (response.success) {
      showAlert(response.message || 'लॉगिन सफल!', 'success');

      const userData = response.data?.customer || response.data;

      const isNameMissing = !userData?.name || userData?.name === String(userData?.mobile);

      const isProfileIncomplete = response.data?.requires_profile_completion || isNameMissing;

      const returnUrl = sessionStorage.getItem('returnUrl') || redirectParam;

      let redirectTo = '/my-profile'; 

      if (isProfileIncomplete) {
        redirectTo = '/complete-profile';
      } else if (returnUrl) {
        redirectTo = returnUrl;
        sessionStorage.removeItem('returnUrl');
      }

      setTimeout(() => {
        navigate(redirectTo);
      }, 1000);

    } else {
      showAlert(response.message || 'गलत OTP दर्ज किया गया है।', 'error');
    }
  };

  // 🌟 API Integration: Resend OTP 🌟
  const handleResendOTP = async () => {
    if (resendTimer > 0) return;
    setLocalLoading(true);

    const response = await resendOTP({
      mobile: mobile,
      country_code: parseInt(countryCode.replace('+', ''))
    });

    setLocalLoading(false);

    if (response.success) {
      setResendTimer(60);
      showAlert(response.message || 'OTP दोबारा भेज दिया गया है।', 'success');
    } else {
      showAlert(response.message || 'OTP दोबारा भेजने में विफल।', 'error');
    }
  };

  const goBackToStep1 = () => {
    setStep(1);
    setOtp('');
    setResendTimer(0);
  };

  return (
    <Box sx={{ bgcolor: bgSoft, minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2, position: 'relative', overflow: 'hidden' }}>

      {/* Decorative Background Shapes */}
      <Box sx={{ position: 'absolute', top: '-15%', left: '-10%', width: '50vw', height: '50vw', background: primaryGradient, opacity: 0.05, borderRadius: '50%', filter: 'blur(60px)' }} />
      <Box sx={{ position: 'absolute', bottom: '-15%', right: '-10%', width: '40vw', height: '40vw', background: '#3B82F6', opacity: 0.05, borderRadius: '50%', filter: 'blur(60px)' }} />

      <Container maxWidth="xs" sx={{ position: 'relative', zIndex: 1, px: 0 }}>
        <Card sx={{ borderRadius: { xs: 4, md: 6 }, boxShadow: '0 25px 50px -12px rgba(0,0,0,0.15)', border: 'none', overflow: 'hidden', bgcolor: 'white' }}>

          {/* 🌟 VIBRANT HEADER AREA 🌟 */}
          <Box sx={{ background: primaryGradient, pt: 4, pb: 6, px: 3, textAlign: 'center', color: 'white', position: 'relative' }}>
            {step === 2 && (
              <IconButton onClick={goBackToStep1} sx={{ position: 'absolute', left: 10, top: 10, color: 'white', bgcolor: 'rgba(255,255,255,0.2)' }}>
                <ArrowBack />
              </IconButton>
            )}

            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
              <Box sx={{ bgcolor: 'white', p: 1.5, borderRadius: '50%', boxShadow: '0 8px 16px rgba(0,0,0,0.2)' }}>
                <Campaign sx={{ fontSize: 40, color: '#EE0979' }} />
              </Box>
            </Box>
            <Typography variant="h5" fontWeight="900" letterSpacing={0.5}>
              PracharApp
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5, fontWeight: 500 }}>
              भारत का No.1 राजनैतिक डिज़ाइन प्लेटफार्म
            </Typography>
          </Box>

          {/* 🌟 MAIN FORM AREA 🌟 */}
          <Box sx={{ p: { xs: 3, md: 4 }, mt: -4, bgcolor: 'white', borderRadius: '30px 30px 0 0', position: 'relative' }}>

            {/* Alert Message */}
            <Box sx={{ minHeight: 50, mb: 1 }}>
              <Fade in={alertInfo.show}>
                <Alert severity={alertInfo.type} sx={{ borderRadius: 2, alignItems: 'center', py: 0 }}>
                  {alertInfo.message}
                </Alert>
              </Fade>
            </Box>

            {/* ----- STEP 1: MOBILE INPUT ----- */}
            {step === 1 && (
              <Fade in={step === 1}>
                <form onSubmit={handleSendOTP}>
                  <Box textAlign="center" mb={4}>
                    <Typography variant="h6" fontWeight="800" color={textDark}>लॉगिन / रजिस्टर करें</Typography>
                    <Typography variant="body2" color={textMuted}>जारी रखने के लिए अपना मोबाइल नंबर दर्ज करें</Typography>
                  </Box>

                  <Box sx={{ display: 'flex', gap: 1, mb: 3 }}>
                    <Select
                      value={countryCode}
                      onChange={(e) => setCountryCode(e.target.value)}
                      sx={{ width: '100px', borderRadius: 3, bgcolor: '#F1F5F9', fontWeight: 'bold', '& fieldset': { border: 'none' }, '&:hover': { bgcolor: '#E2E8F0' } }}
                    >
                      <MenuItem value="+91">🇮🇳 +91</MenuItem>
                      <MenuItem value="+1">🇺🇸 +1</MenuItem>
                    </Select>

                    <TextField
                      fullWidth
                      placeholder="9876543210"
                      value={mobile}
                      onChange={handleMobileChange}
                      autoFocus
                      InputProps={{
                        startAdornment: <InputAdornment position="start"><PhoneAndroid sx={{ color: '#94A3B8' }} /></InputAdornment>,
                        sx: { borderRadius: 3, bgcolor: '#F1F5F9', fontWeight: 'bold', fontSize: '1.1rem', letterSpacing: '1px', '& fieldset': { border: 'none' }, '&:hover': { bgcolor: '#E2E8F0' }, '&.Mui-focused': { bgcolor: 'white', '& fieldset': { border: '2px solid #FF6A00' } } }
                      }}
                    />
                  </Box>

                  <Typography variant="subtitle2" fontWeight="700" color={textDark} mb={1.5}>
                    OTP प्राप्त करने का तरीका चुनें:
                  </Typography>

                  <Stack direction="row" spacing={2} mb={4}>
                    <Paper
                      elevation={0}
                      onClick={() => setOtpMethod('sms')}
                      sx={{ flex: 1, p: 1.5, textAlign: 'center', borderRadius: 3, cursor: 'pointer', border: '2px solid', borderColor: otpMethod === 'sms' ? '#3B82F6' : 'transparent', bgcolor: otpMethod === 'sms' ? '#EFF6FF' : '#F1F5F9', transition: 'all 0.2s' }}
                    >
                      <Message sx={{ color: otpMethod === 'sms' ? '#3B82F6' : '#94A3B8', mb: 0.5, fontSize: 28 }} />
                      <Typography variant="body2" fontWeight="700" color={otpMethod === 'sms' ? '#1E40AF' : textMuted}>SMS</Typography>
                    </Paper>

                    <Paper
                      elevation={0}
                      onClick={() => setOtpMethod('whatsapp')}
                      sx={{ flex: 1, p: 1.5, textAlign: 'center', borderRadius: 3, cursor: 'pointer', border: '2px solid', borderColor: otpMethod === 'whatsapp' ? '#10B981' : 'transparent', bgcolor: otpMethod === 'whatsapp' ? '#ECFDF5' : '#F1F5F9', transition: 'all 0.2s' }}
                    >
                      <WhatsApp sx={{ color: otpMethod === 'whatsapp' ? '#10B981' : '#94A3B8', mb: 0.5, fontSize: 28 }} />
                      <Typography variant="body2" fontWeight="700" color={otpMethod === 'whatsapp' ? '#065F46' : textMuted}>WhatsApp</Typography>
                    </Paper>
                  </Stack>

                  <Button
                    type="submit" fullWidth disabled={localLoading || mobile.length < 10}
                    sx={{ background: primaryGradient, color: 'white', py: 1.8, borderRadius: 50, fontSize: '1.1rem', fontWeight: '900', boxShadow: '0 8px 20px rgba(238, 9, 121, 0.3)', '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 12px 25px rgba(238, 9, 121, 0.4)' }, '&.Mui-disabled': { background: '#E2E8F0', color: '#94A3B8', boxShadow: 'none' } }}
                  >
                    {localLoading ? <CircularProgress size={26} color="inherit" /> : 'OTP भेजें'}
                  </Button>
                </form>
              </Fade>
            )}

            {/* ----- STEP 2: OTP VERIFICATION ----- */}
            {step === 2 && (
              <Fade in={step === 2}>
                <form onSubmit={handleVerifyOTP}>
                  <Box textAlign="center" mb={4}>
                    <Typography variant="h6" fontWeight="800" color={textDark}>OTP दर्ज करें</Typography>
                    <Typography variant="body2" color={textMuted} mb={1}>हमने <b>{countryCode} {mobile}</b> पर एक कोड भेजा है</Typography>
                  </Box>

                  <Box mb={4}>
                    <TextField
                      fullWidth autoFocus placeholder="• • • • • •"
                      value={otp} onChange={handleOtpChange}
                      InputProps={{
                        sx: { borderRadius: 3, bgcolor: '#F1F5F9', fontWeight: '900', fontSize: '2rem', letterSpacing: '0.8rem', textAlign: 'center', '& fieldset': { border: 'none' }, '&.Mui-focused': { bgcolor: 'white', '& fieldset': { border: '2px solid #FF6A00' } }, '& input': { textAlign: 'center' } }
                      }}
                    />
                  </Box>

                  <Button
                    type="submit" fullWidth disabled={localLoading || otp.length < 6}
                    startIcon={!localLoading && <CheckCircle />}
                    sx={{ background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)', color: 'white', py: 1.8, borderRadius: 50, mb: 3, fontSize: '1.1rem', fontWeight: '900', boxShadow: '0 8px 20px rgba(16, 185, 129, 0.3)', '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 12px 25px rgba(16, 185, 129, 0.4)' }, '&.Mui-disabled': { background: '#E2E8F0', color: '#94A3B8', boxShadow: 'none' } }}
                  >
                    {localLoading ? <CircularProgress size={26} color="inherit" /> : 'सत्यापित करें'}
                  </Button>

                  <Box textAlign="center">
                    <Typography variant="body2" color={textMuted} mb={1}>Didn't receive the OTP?</Typography>
                    <Button
                      onClick={handleResendOTP} disabled={resendTimer > 0 || localLoading} startIcon={<LockReset />}
                      sx={{ fontWeight: '800', color: '#FF6A00', '&.Mui-disabled': { color: '#94A3B8' } }}
                    >
                      {resendTimer > 0 ? `Resend OTP in ${resendTimer}s` : 'Resend OTP Now'}
                    </Button>
                  </Box>
                </form>
              </Fade>
            )}

          </Box>

          {/* Footer */}
          <Box sx={{ bgcolor: '#F8FAFC', py: 2, textAlign: 'center', borderTop: '1px solid #e2e8f0' }}>
            <Typography variant="caption" color="#94A3B8" fontWeight="600" display="flex" alignItems="center" justifyContent="center">
              <Shield fontSize="small" sx={{ mr: 0.5, mb: '2px', color: '#10B981' }} /> 100% Secure & Encrypted
            </Typography>
          </Box>

        </Card>
      </Container>
    </Box>
  );
}